import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { useAuth, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Plus, 
  Moon,
  Droplets,
  Brain,
  Battery,
  Trash2,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle2,
  Loader2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const RecoveryPage = () => {
  const [searchParams] = useSearchParams();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewDialog, setShowNewDialog] = useState(searchParams.get('action') === 'new');
  
  // Hydration - now fetched from API (shared with Systems page)
  const [hydration, setHydration] = useState(0);
  const [hydrationTarget, setHydrationTarget] = useState(100); // oz

  // Recovery activities completion tracking
  const [completedActivities, setCompletedActivities] = useState(() => {
    const today = new Date().toLocaleDateString('en-CA'); // Local date
    const saved = localStorage.getItem(`aura_recovery_activities_${today}`);
    return saved ? JSON.parse(saved) : [];
  });
  const [completingActivity, setCompletingActivity] = useState(null);

  const [newLog, setNewLog] = useState({
    date: new Date().toLocaleDateString('en-CA'), // Local date
    sleep_hours: 8,
    sleep_quality: 7,
    stress_level: 3,
    fatigue_level: 3,
    soreness_areas: [],
    notes: ''
  });

  const sorenessOptions = [
    'Neck', 'Shoulders', 'Upper Back', 'Lower Back', 
    'Arms', 'Chest', 'Core', 'Hips', 
    'Quads', 'Hamstrings', 'Calves', 'Ankles'
  ];

  useEffect(() => {
    fetchRecoveryLogs();
    fetchHydration(); // NEW: Fetch hydration from API
  }, []);
  
  // NEW: Fetch hydration from shared API
  const fetchHydration = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const today = new Date().toLocaleDateString('en-CA');
      const response = await axios.get(`${API}/hydration/today?date=${today}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHydration(response.data.total_oz || 0);
      setHydrationTarget(response.data.goal_oz || 100);
    } catch (error) {
      console.error('Failed to fetch hydration:', error);
    }
  };

  const fetchRecoveryLogs = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/recovery`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch recovery logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLog = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('aura_token');
      await axios.post(`${API}/recovery`, newLog, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success('Recovery logged. Stay consistent.');
      setShowNewDialog(false);
      setNewLog({
        date: new Date().toLocaleDateString('en-CA'), // Local date
        sleep_hours: 8,
        sleep_quality: 7,
        stress_level: 3,
        fatigue_level: 3,
        soreness_areas: [],
        notes: ''
      });
      fetchRecoveryLogs();
    } catch (error) {
      toast.error('Failed to log recovery');
    }
  };

  const handleDeleteLog = async (logId) => {
    try {
      const token = localStorage.getItem('aura_token');
      await axios.delete(`${API}/recovery/${logId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success('Log deleted');
      fetchRecoveryLogs();
    } catch (error) {
      toast.error('Failed to delete log');
    }
  };

  const toggleSoreness = (area) => {
    setNewLog(prev => ({
      ...prev,
      soreness_areas: prev.soreness_areas.includes(area)
        ? prev.soreness_areas.filter(a => a !== area)
        : [...prev.soreness_areas, area]
    }));
  };

  const addHydration = async (amount) => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.post(`${API}/hydration/log`, { amount_oz: amount }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHydration(response.data.total_oz);
      toast.success(`+${amount}oz logged!`);
    } catch (error) {
      console.error('Failed to log hydration:', error);
      toast.error('Failed to log hydration');
    }
  };

  // Handle completing a recovery activity
  const handleCompleteActivity = async (activityId, activityName, points = 5) => {
    if (completedActivities.includes(activityId)) {
      // Already completed - do nothing or toggle off
      return;
    }
    
    setCompletingActivity(activityId);
    
    try {
      const token = localStorage.getItem('aura_token');
      // Log to backend for points
      await axios.post(`${API}/recovery/activity/complete`, {
        activity_id: activityId,
        activity_name: activityName,
        activity_type: 'stretch',
        points: points
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local state
      const newCompleted = [...completedActivities, activityId];
      setCompletedActivities(newCompleted);
      
      // Persist to localStorage with local date
      const today = new Date().toLocaleDateString('en-CA');
      localStorage.setItem(`aura_recovery_activities_${today}`, JSON.stringify(newCompleted));
      
      toast.success(`+${points} pts - ${activityName} complete!`);
    } catch (error) {
      // Still mark as complete locally even if API fails
      const newCompleted = [...completedActivities, activityId];
      setCompletedActivities(newCompleted);
      const today = new Date().toLocaleDateString('en-CA');
      localStorage.setItem(`aura_recovery_activities_${today}`, JSON.stringify(newCompleted));
      toast.success(`${activityName} complete!`);
    } finally {
      setCompletingActivity(null);
    }
  };

  // Calculate 7-day trend
  const getWeeklyTrend = () => {
    if (logs.length < 2) return { trend: 0, direction: 'stable' };
    const recent = logs.slice(0, 7);
    if (recent.length < 2) return { trend: 0, direction: 'stable' };
    
    const avgRecent = recent.slice(0, 3).reduce((sum, log) => sum + log.recovery_score, 0) / Math.min(3, recent.length);
    const avgOlder = recent.slice(3).reduce((sum, log) => sum + log.recovery_score, 0) / Math.max(1, recent.slice(3).length);
    
    const trend = Math.round(avgRecent - avgOlder);
    return {
      trend: Math.abs(trend),
      direction: trend > 2 ? 'up' : trend < -2 ? 'down' : 'stable'
    };
  };

  const weeklyTrend = getWeeklyTrend();

  const getRecoveryColor = (score) => {
    if (score >= 80) return 'text-aura-success';
    if (score >= 60) return 'text-aura-secondary';
    if (score >= 40) return 'text-aura-warning';
    return 'text-aura-destructive';
  };

  const getRecoveryBg = (score) => {
    if (score >= 80) return 'bg-aura-success/20';
    if (score >= 60) return 'bg-aura-secondary/20';
    if (score >= 40) return 'bg-aura-warning/20';
    return 'bg-aura-destructive/20';
  };

  const latestLog = logs[0];

  // Morning Stretch Flow from your docs
  const morningStretch = [
    { step: 1, exercise: 'Neck Rolls', reps: '5 each direction' },
    { step: 2, exercise: 'Arm Circles', reps: '10 forward/back' },
    { step: 3, exercise: 'Torso Twists', reps: '10 each side' },
    { step: 4, exercise: 'Hip Opener (Butterfly)', reps: '20 sec hold' },
    { step: 5, exercise: 'Hamstring Stretch', reps: '20 sec each leg' },
    { step: 6, exercise: 'Quad Stretch', reps: '20 sec each leg' },
    { step: 7, exercise: 'Calf Stretch', reps: '20 sec each leg' },
    { step: 8, exercise: 'Walking Lunges', reps: '10 steps each leg' },
  ];

  return (
    <DashboardLayout title="Recovery System">
      <div className="px-4 py-6 max-w-7xl mx-auto space-y-6">
        {/* Section Header */}
        <div className="mb-2">
          <p className="text-aura-text-muted text-sm">Recovery drives performance.</p>
        </div>
        
        {/* Recovery Score Card */}
        {latestLog && (
          <Card className={`border-white/10 ${getRecoveryBg(latestLog.recovery_score)}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-aura-text-muted uppercase tracking-wider mb-1">Recovery Score</p>
                  <div className="flex items-baseline gap-2">
                    <span className={`font-heading font-bold text-5xl ${getRecoveryColor(latestLog.recovery_score)}`}>
                      {latestLog.recovery_score}
                    </span>
                    <span className="text-aura-text-muted">/100</span>
                  </div>
                  {weeklyTrend.direction !== 'stable' && (
                    <div className={`flex items-center gap-1 mt-2 text-sm ${
                      weeklyTrend.direction === 'up' ? 'text-aura-success' : 'text-aura-destructive'
                    }`}>
                      {weeklyTrend.direction === 'up' ? (
                        <TrendingUp className="w-4 h-4" />
                      ) : (
                        <TrendingDown className="w-4 h-4" />
                      )}
                      <span>{weeklyTrend.trend} pts this week</span>
                    </div>
                  )}
                </div>
                <div className="text-right space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Moon className="w-4 h-4 text-aura-secondary" />
                    <span className="text-white">{latestLog.sleep_hours}h sleep</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Brain className="w-4 h-4 text-aura-warning" />
                    <span className="text-white">Stress: {latestLog.stress_level}/10</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Battery className="w-4 h-4 text-aura-primary" />
                    <span className="text-white">Fatigue: {latestLog.fatigue_level}/10</span>
                  </div>
                </div>
              </div>
              {latestLog.recovery_score < 50 && (
                <div className="mt-4 flex items-start gap-2 p-3 rounded-lg bg-aura-destructive/20">
                  <AlertTriangle className="w-5 h-5 text-aura-destructive flex-shrink-0" />
                  <p className="text-sm text-white">
                    Low recovery score. Consider a lighter workout or rest day today.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* 7-Day Recovery Trend */}
        {logs.length > 0 && (
          <Card className="bg-aura-surface border-white/10">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading font-bold text-sm text-aura-text-muted uppercase tracking-wider">
                7-Day Recovery Trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-end justify-between gap-2 h-24">
                {logs.slice(0, 7).reverse().map((log, index) => {
                  const height = Math.max(10, (log.recovery_score / 100) * 100);
                  return (
                    <div key={log.log_id} className="flex-1 flex flex-col items-center gap-1">
                      <div 
                        className={`w-full rounded-t transition-all ${getRecoveryBg(log.recovery_score).replace('/20', '/40')}`}
                        style={{ height: `${height}%` }}
                      />
                      <span className="text-[10px] text-aura-text-subtle">
                        {new Date(log.date).toLocaleDateString('en-US', { weekday: 'short' }).slice(0, 2)}
                      </span>
                    </div>
                  );
                })}
                {/* Fill empty slots if less than 7 days */}
                {Array.from({ length: Math.max(0, 7 - logs.length) }).map((_, i) => (
                  <div key={`empty-${i}`} className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full h-2 bg-white/5 rounded-t" />
                    <span className="text-[10px] text-aura-text-subtle">--</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
            <DialogTrigger asChild>
              <Button className="flex-1 sm:flex-none bg-aura-secondary hover:bg-aura-secondary/90 text-white font-heading uppercase tracking-wider h-12" data-testid="log-recovery-btn">
                <Plus className="w-5 h-5 mr-2" />
                Log Recovery
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-aura-surface border-white/10 max-w-md mx-4 max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading font-bold text-xl text-white uppercase tracking-wider">
                  Log Recovery Data
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateLog} className="space-y-6 mt-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Date</Label>
                  <Input
                    type="date"
                    value={newLog.date}
                    onChange={(e) => setNewLog({ ...newLog, date: e.target.value })}
                    className="h-12 bg-aura-bg border-white/10 text-white"
                  />
                </div>

                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Sleep Hours</Label>
                      <span className="font-heading font-bold text-lg text-white">{newLog.sleep_hours}h</span>
                    </div>
                    <Slider
                      value={[newLog.sleep_hours]}
                      onValueChange={([v]) => setNewLog({ ...newLog, sleep_hours: v })}
                      min={0}
                      max={12}
                      step={0.5}
                      className="py-4"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Sleep Quality</Label>
                      <span className="font-heading font-bold text-lg text-white">{newLog.sleep_quality}/10</span>
                    </div>
                    <Slider
                      value={[newLog.sleep_quality]}
                      onValueChange={([v]) => setNewLog({ ...newLog, sleep_quality: v })}
                      min={1}
                      max={10}
                      step={1}
                      className="py-4"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Stress Level</Label>
                      <span className="font-heading font-bold text-lg text-aura-warning">{newLog.stress_level}/10</span>
                    </div>
                    <Slider
                      value={[newLog.stress_level]}
                      onValueChange={([v]) => setNewLog({ ...newLog, stress_level: v })}
                      min={1}
                      max={10}
                      step={1}
                      className="py-4"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Fatigue Level</Label>
                      <span className="font-heading font-bold text-lg text-aura-primary">{newLog.fatigue_level}/10</span>
                    </div>
                    <Slider
                      value={[newLog.fatigue_level]}
                      onValueChange={([v]) => setNewLog({ ...newLog, fatigue_level: v })}
                      min={1}
                      max={10}
                      step={1}
                      className="py-4"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Soreness Areas</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {sorenessOptions.map((area) => (
                      <button
                        key={area}
                        type="button"
                        onClick={() => toggleSoreness(area)}
                        className={`p-2 text-xs rounded-lg border transition-colors ${
                          newLog.soreness_areas.includes(area)
                            ? 'bg-aura-destructive/20 border-aura-destructive text-white'
                            : 'border-white/10 text-aura-text-muted hover:border-white/20'
                        }`}
                      >
                        {area}
                      </button>
                    ))}
                  </div>
                </div>

                <Button type="submit" className="w-full bg-aura-secondary hover:bg-aura-secondary/90 text-white font-heading uppercase tracking-wider h-12" data-testid="save-recovery-btn">
                  Save Recovery Log
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Hydration Tracker */}
        <Card className="bg-aura-surface border-white/10">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
                <Droplets className="w-5 h-5 text-aura-secondary" />
                Hydration
              </CardTitle>
              <span className="text-sm text-aura-text-muted">{hydration}/{hydrationTarget} oz</span>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Progress value={(hydration / hydrationTarget) * 100} className="h-3 bg-white/10" />
            <div className="grid grid-cols-4 gap-2">
              {[8, 12, 16, 20].map((amount) => (
                <Button
                  key={amount}
                  variant="outline"
                  onClick={() => addHydration(amount)}
                  className="border-white/10 text-white hover:bg-aura-secondary/20 h-12"
                >
                  +{amount}oz
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Morning Stretch Flow */}
        <Card className="bg-aura-surface border-white/10">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider">
                  Morning Stretch Flow
                </CardTitle>
                <p className="text-sm text-aura-text-muted">7-10 minutes daily • Tap to complete</p>
              </div>
              <div className="text-right">
                <span className="text-2xl font-heading font-bold text-aura-secondary">
                  {completedActivities.filter(id => id.startsWith('stretch_')).length}/{morningStretch.length}
                </span>
                <p className="text-xs text-aura-text-muted">completed</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {morningStretch.map((item) => {
                const activityId = `stretch_${item.step}`;
                const isCompleted = completedActivities.includes(activityId);
                const isLoading = completingActivity === activityId;
                
                return (
                  <button 
                    key={item.step}
                    onClick={() => handleCompleteActivity(activityId, item.exercise, 5)}
                    disabled={isCompleted || isLoading}
                    className={`w-full flex items-center gap-4 p-3 rounded-lg transition-all text-left ${
                      isCompleted 
                        ? 'bg-aura-success/20 border-2 border-aura-success/50' 
                        : 'bg-white/5 hover:bg-white/10 border-2 border-transparent hover:border-aura-secondary/30'
                    }`}
                    data-testid={`stretch-${item.step}`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isCompleted ? 'bg-aura-success' : 'bg-aura-secondary/20'
                    }`}>
                      {isLoading ? (
                        <Loader2 className="w-4 h-4 text-white animate-spin" />
                      ) : isCompleted ? (
                        <CheckCircle2 className="w-5 h-5 text-white" />
                      ) : (
                        <span className="font-heading font-bold text-aura-secondary">{item.step}</span>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className={`font-medium ${isCompleted ? 'text-aura-success line-through' : 'text-white'}`}>
                        {item.exercise}
                      </p>
                      <p className="text-sm text-aura-text-muted">{item.reps}</p>
                    </div>
                    {!isCompleted && (
                      <span className="text-xs text-aura-text-muted">+5 pts</span>
                    )}
                  </button>
                );
              })}
            </div>
            
            {/* Complete All Button */}
            {completedActivities.filter(id => id.startsWith('stretch_')).length < morningStretch.length && (
              <Button
                onClick={() => {
                  morningStretch.forEach((item) => {
                    const activityId = `stretch_${item.step}`;
                    if (!completedActivities.includes(activityId)) {
                      handleCompleteActivity(activityId, item.exercise, 5);
                    }
                  });
                }}
                className="w-full mt-4 bg-aura-secondary hover:bg-aura-secondary/90 text-white font-heading uppercase tracking-wider"
              >
                Complete All (+{(morningStretch.length - completedActivities.filter(id => id.startsWith('stretch_')).length) * 5} pts)
              </Button>
            )}
            
            {completedActivities.filter(id => id.startsWith('stretch_')).length === morningStretch.length && (
              <div className="mt-4 p-3 bg-aura-success/20 rounded-lg text-center">
                <CheckCircle2 className="w-6 h-6 text-aura-success mx-auto mb-1" />
                <p className="text-aura-success font-medium">Morning Stretch Complete!</p>
                <p className="text-xs text-aura-text-muted">+{morningStretch.length * 5} pts earned today</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Recovery Logs */}
        <div>
          <h2 className="font-heading font-bold text-lg text-white uppercase tracking-wider mb-4">
            Recovery History
          </h2>
          
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="aura-card p-4 animate-pulse">
                  <div className="h-16 bg-white/5 rounded-lg" />
                </div>
              ))}
            </div>
          ) : logs.length > 0 ? (
            <div className="space-y-3">
              {logs.map((log) => (
                <Card key={log.log_id} className="bg-aura-surface border-white/10">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${getRecoveryBg(log.recovery_score)}`}>
                          <span className={`font-heading font-bold text-lg ${getRecoveryColor(log.recovery_score)}`}>
                            {log.recovery_score}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-white">{log.date}</p>
                          <div className="flex flex-wrap gap-3 mt-1 text-sm text-aura-text-muted">
                            <span>{log.sleep_hours}h sleep</span>
                            <span>Quality: {log.sleep_quality}/10</span>
                            <span>Stress: {log.stress_level}/10</span>
                          </div>
                          {log.soreness_areas.length > 0 && (
                            <p className="text-xs text-aura-destructive mt-1">
                              Sore: {log.soreness_areas.join(', ')}
                            </p>
                          )}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteLog(log.log_id)}
                        className="text-aura-text-muted hover:text-aura-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="bg-aura-surface border-white/10">
              <CardContent className="p-8 text-center">
                <Moon className="w-12 h-12 text-aura-text-subtle mx-auto mb-4" />
                <h3 className="font-heading font-bold text-lg text-white mb-2">Recovery is Part of Performance</h3>
                <p className="text-aura-text-muted mb-4">
                  Recovery is part of performance. Stay consistent.
                </p>
                <Button 
                  onClick={() => setShowNewDialog(true)}
                  className="bg-aura-secondary hover:bg-aura-secondary/90 text-white"
                >
                  Log First Recovery
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default RecoveryPage;
